import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { List, CreateButton, FilterButton, Button, RestMethods } from "jazasoft";

import UploadIcon from "mdi-material-ui/Upload";
import ListIcon from "mdi-material-ui/FormatListBulleted";
import HierarchyIcon from "mdi-material-ui/Segment";
import { Card } from "@material-ui/core";

import MuiTable from "../../../components/MuiTable";

import hasPrivilege from "../../../utils/hasPrivilege";
import filters from "../../OrderFilter";
import useQuery from "../../../hooks/useQuery";
import SettingKeys from "../../../utils/SettingKeys";
import { OState } from "../../../utils/types";
import { getDistinctValues, isEmpty } from "../../../utils/helpers";
import useMutation from "../../../hooks/useMutation";
import { downloadOrders } from "./helper";
import { useEntityPrivilege, useSetting, useUnit } from "../../../hooks";
import { dataProvider } from "../../../App";
import { HomeView } from "../../../constants";
import { ResourceId, Scope } from "../../../utils/privilege";

const OrderStateValue = Object.keys(OState)
  .map(key => OState[key])
  .reduce((acc, e) => ({ ...acc, [e.state]: e.stateValue }), {});

const orderState = order => {
  const stateList = order?.states?.split(",");
  const state = stateList?.map(state => ({ state, value: OrderStateValue[state] })).sort((a, b) => a.value - b.value)[0]?.state;
  return state + `${stateList?.length > 1 ? "*" : ""}`;
};

const flowInfoState = flowInfo => {
  const stateList = JSON.parse(flowInfo.state);
  if (stateList?.length === 1) {
    return stateList[0]?.state;
  } else if (stateList?.length > 1) {
    const states = stateList.map(e => e.state);
    const effState = Object.values(OState)
      .filter(e => states.includes(e.state))
      .sort((a, b) => a.stateValue - b.stateValue)[0]?.state;
    return `${effState}*`;
  }
  return "";
};

const CustomDatagrid = props => {
  const { view, data, ids, history, orderFields = [], flowInfoFields = [], basePath, download, translate } = props;

  const selectActions = [
    {
      name: "exportShell",
      showLabel: true,
      tooltip: translate("resources.orders.button.export_shell", { _: "Export Shell" }),
      options: { variant: "outlined", color: "primary", style: { marginRight: "1em" } }
    },
    {
      name: "export",
      showLabel: true,
      tooltip: translate("resources.orders.button.export", { _: "Export" }),
      options: { variant: "contained", color: "primary", style: { marginRight: "1em" } }
    }
  ];

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    const selectedIds = selectedRows.map(row => row.id);
    if (action === "exportShell") {
      const shellOnly = true;
      downloadOrders && downloadOrders(shellOnly, selectedIds, download);
    } else if (action === "export") {
      const shellOnly = false;
      downloadOrders && downloadOrders(shellOnly, selectedIds, download);
    }
    onActionComplete && onActionComplete(rows);
  };
  const fields = [...orderFields, ...flowInfoFields];
  const columns = [
    { dataKey: "poRef", title: "PO Ref. No" },
    { dataKey: "customerName", title: "Customer" },
    { dataKey: "productName", title: "Product" },
    { dataKey: "seasonName", title: "Season" },
    { dataKey: "style", title: "Style" },
    { dataKey: "color", title: "Color" },
    { dataKey: "inseam", title: "Inseam", render: (value, shortValue) => shortValue || "-" },
    { dataKey: "destination", title: "Destination", render: (value, shortValue) => shortValue || "-" },
    { dataKey: "deliveryDate", title: "Delivery Date", render: (value, shortValue) => shortValue || "-" },
    { dataKey: "customerFlowRef", title: "Customer Flow Ref", render: (value, shortValue) => shortValue || "-" },
    { dataKey: "flowRef", title: "Flow Ref", render: (value, shortValue) => shortValue || "-" },
    { dataKey: "delFlowRef", title: "Del. Flow Ref", render: (value, shortValue) => shortValue || "-" },
    { dataKey: "orderQty", title: "Order Qty" },
    { dataKey: "status", title: "Status" }
  ].filter(c => isEmpty(fields) || fields.includes(c.dataKey));

  const rows = ids
    .map(id => data[id])
    .map(row => {
      if (view === HomeView.HIERARCHY) {
        const status = orderState(row);
        return {
          ...row,
          key: `order-${row.id}`,
          productName: row.products,
          customerName: row.customer?.name,
          seasonName: row.season?.name,
          style: row.styles,
          color: row.colors,
          inseam: row.inseams,
          destination: row.destinations,
          deliveryDate: row.deliveryDates,
          customerFlowRef: row.customerFlowRefs,
          flowRef: row.flowRefs,
          delFlowRef: row.delFlowRefs,
          status,
          hasChild: row.flowInfoCount > 1
        };
      } else {
        const status = flowInfoState(row);
        return {
          ...row,
          key: row.id,
          productName: row.product?.name,
          status
        };
      }
    });

  const fetchChildren = async row => {
    const options = {
      url: "flow-info",
      method: "get",
      params: { search: `order.id==${row.id};orderParentId=='NULL'` }
    };
    const response = await dataProvider(RestMethods.CUSTOM, null, options);

    const data = response.data && response.data.content && Array.isArray(response.data.content) ? response.data.content : response.data;

    const flowInfoList = data.map(flowInfo => {
      const children = data.filter(fi => fi.parentId === flowInfo.id);
      const inseam = isEmpty(children) ? flowInfo.inseam : getDistinctValues(children.map(c => c.inseam).filter(e => !isEmpty(e))).join(", ");
      const destination = isEmpty(children)
        ? flowInfo.destination
        : getDistinctValues(children.map(c => c.destination).filter(e => !isEmpty(e))).join(", ");
      const deliveryDate = isEmpty(children)
        ? flowInfo.deliveryDate
        : getDistinctValues(children.map(c => c.deliveryDate).filter(e => !isEmpty(e))).join(", ");
      const flowRef = isEmpty(children) ? flowInfo.flowRef : getDistinctValues(children.map(c => c.flowRef).filter(e => !isEmpty(e))).join(", ");
      const delFlowRef = isEmpty(children) ? flowInfo.delFlowRef : getDistinctValues(children.map(c => c.delFlowRef).filter(e => !isEmpty(e))).join(", ");
      const customerFlowRef = isEmpty(children)
        ? flowInfo.customerFlowRef
        : getDistinctValues(children.map(c => c.customerFlowRef).filter(e => !isEmpty(e))).join(", ");
      return {
        ...flowInfo,
        orderId: row.id,
        poRef: row.poRef,
        customerName: row.customerName,
        seasonName: row.seasonName,
        productName: flowInfo.product?.name,
        inseam,
        destination,
        deliveryDate,
        flowRef,
        delFlowRef,
        customerFlowRef,
        status: flowInfoState(flowInfo),
        key: `flow-${flowInfo.id}`,
        parentKey: isEmpty(flowInfo.parentId) ? row.key : `flow-${flowInfo.parentId}`
      };
    });
    return flowInfoList;
  };

  return (
    <div>
      <Card>
        <MuiTable
          columns={columns}
          rows={rows}
          fontSize={16}
          selectable={view === HomeView.HIERARCHY}
          selectActions={selectActions}
          onSelectActionClick={onSelectActionClick}
          expandedColor={["#BFDCF8", "#D6E9FB", "#EDF5FD"]}
          idKey="key"
          parentIdKey="parentKey"
          isTreeTable={view === HomeView.HIERARCHY}
          i18nKey="resources.orders.fields"
          fetchChildren={fetchChildren}
          onRowClick={row => history.push(`${basePath}/${row.orderId || row.id}/view`)}
        />
      </Card>
    </div>
  );
};

const Order = props => {
  const { basePath } = props;
  const [view, setView] = useState(HomeView.HIERARCHY); // values: hierarchy, flat
  const { disableCreate } = useEntityPrivilege("order");

  const { data: productList = [] } = useQuery({ url: "products", params: { size: 1000, sort: "name,asc" } });

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, "orders");
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");

  const { allUnitList } = useUnit();
  const unitChoices = [{ id: "all", name: "All Units" }, ...allUnitList.map(({ unitId, name }) => ({ id: unitId, name }))];

  const history = useHistory();
  const [download] = useMutation({ rawResponse: true });

  return (
    <List
      {...props}
      key={`${view}-${disableCreate}`}
      resource={view === HomeView.HIERARCHY ? "orders" : "flow-info"}
      searchKeys={
        view === HomeView.HIERARCHY
          ? ["poRef", "customerPoRef", "styles", "flowRefs", "delFlowRefs", "customerFlowRefs"]
          : ["order.poRef", "order.customerPoRef", "style", "flowRef", "delFlowRefs", "customerFlowRef"]
      }
      actions={({ roles, hasAccess }) => (
        <div>
          {hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.WRITE) && !disableCreate && <CreateButton basePath={basePath} showLabel={false} />}
          {hasPrivilege(roles, hasAccess, ResourceId.ORDER, Scope.WRITE) && !disableCreate && (
            <Button showLabel={false} label={"Excel Upload"} onClick={() => history && history.push(`${basePath}/upload`)}>
              <UploadIcon />
            </Button>
          )}
          <FilterButton showLabel={false} />
          <Button
            showLabel={false}
            label={view === HomeView.HIERARCHY ? "Hierarchy View" : "List View"}
            onClick={() => setView(prev => (prev === HomeView.HIERARCHY ? "flat" : HomeView.HIERARCHY))}>
            {view === HomeView.HIERARCHY ? <HierarchyIcon /> : <ListIcon />}
          </Button>
        </div>
      )}
      filters={filters(true, productList, unitChoices)}
      parse={({ unitId, customerId, seasonId, productName, state, line }) => {
        if (view === HomeView.HIERARCHY) {
          return {
            allocUnitIds: unitId === "all" ? unitId : !isEmpty(unitId) ? `*${unitId}*` : null,
            "customer.id": customerId,
            "season.id": seasonId,
            products: isEmpty(productName) ? null : `*${productName}*`,
            states: state,
            lines: line ? `*${line}*` : null
          };
        } else {
          return {
            primaryUnitId: unitId === "all" ? unitId : !isEmpty(unitId) ? `*${unitId}*` : null,
            "order.customer.id": customerId,
            "order.season.id": seasonId,
            "product.name": productName,
            state: state ? `*${state}*` : null,
            "order.lines": line ? `*${line}*` : null,
            isLeaf: "TRUE",
            orderParentId: "NULL"
          };
        }
      }}>
      <CustomDatagrid
        history={history}
        download={download}
        orderFields={orderFields}
        basePath={basePath}
        flowInfoFields={flowInfoFields}
        view={view}
      />
    </List>
  );
};

export default Order;
